import { useMemo } from "react";
import {
    ArrowDownLeft, ArrowUpRight, AlertTriangle, Eye, Inbox, Mail, MailOpen, MessageSquareReply,
    MousePointerClick, Paperclip, PenSquare, RefreshCw, Search, Send, Clock,
} from "lucide-react";
import { cn } from "../../../utils/tailwindUtils";
import {
    avatarTone, dateBucket, fromNow, groupThreads, initials, makePersonResolver, shortTime, firstAddress,
} from "./emailThreads";

/**
 * The lead page's Emails tab: an overview of the email relationship, the
 * toolbar, and the conversation list. State lives in CustomerDetails (filters,
 * open email); this component only draws it. Opening a conversation calls
 * onOpen(latestId) and the reading panel (EmailReader) shows the whole thread.
 */

function Stat({ icon: Icon, label, value, hint, tone = "text-gray-900 dark:text-white" }) {
    return (
        <div className="min-w-0 rounded-xl border border-gray-200 bg-white px-3.5 py-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <div className={cn("mt-1 text-lg font-semibold leading-tight", tone)}>{value}</div>
            {hint && <div className="mt-0.5 truncate text-[11px] text-gray-400 dark:text-gray-500">{hint}</div>}
        </div>
    );
}

function Overview({ emails, person }) {
    const sent = emails.filter(e => e.direction === "sent");
    const received = emails.filter(e => e.direction === "received");
    const tracked = sent.filter(e => !e.is_bounced);
    const openRate = tracked.length ? Math.round(tracked.filter(e => e.is_opened).length / tracked.length * 100) : null;
    const replyRate = sent.length ? Math.round(sent.filter(e => e.is_replied).length / sent.length * 100) : null;
    const latest = emails.reduce((a, b) => (!a || new Date(b.sent_at || 0) > new Date(a.sent_at || 0) ? b : a), null);

    // The one thing a rep should act on: who owes whom a reply.
    let nudge = null;
    if (latest?.direction === "received") {
        nudge = { tone: "amber", icon: MessageSquareReply,
                  text: `${person(latest.from_email, latest).name} wrote ${fromNow(latest.sent_at)} — awaiting your reply` };
    } else if (latest?.direction === "sent" && !latest.is_replied) {
        nudge = latest.is_opened
            ? { tone: "sky", icon: Eye, text: `Your last email was opened ${fromNow(latest.opened_at || latest.sent_at)} — no reply yet` }
            : { tone: "gray", icon: Clock, text: `Your last email went out ${fromNow(latest.sent_at)} — not opened yet` };
    }
    const nudgeTone = {
        amber: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200",
        sky: "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800/60 dark:bg-sky-900/20 dark:text-sky-200",
        gray: "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300",
    };

    return (
        <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                <Stat icon={Mail} label="Emails" value={emails.length}
                    hint={`${sent.length} sent · ${received.length} received`} />
                <Stat icon={MailOpen} label="Open rate" value={openRate === null ? "—" : `${openRate}%`}
                    hint={sent.length ? `${tracked.filter(e => e.is_opened).length} of ${tracked.length} opened` : "Nothing sent yet"}
                    tone={openRate >= 40 ? "text-emerald-600 dark:text-emerald-400" : undefined} />
                <Stat icon={MessageSquareReply} label="Reply rate" value={replyRate === null ? "—" : `${replyRate}%`}
                    hint={received.length ? `${received.length} repl${received.length === 1 ? "y" : "ies"} in` : "No replies yet"}
                    tone={replyRate >= 20 ? "text-emerald-600 dark:text-emerald-400" : undefined} />
                <Stat icon={Clock} label="Last contact" value={latest ? fromNow(latest.sent_at) : "—"}
                    hint={latest ? (latest.direction === "sent" ? "You emailed them" : "They emailed you") : ""} />
            </div>
            {nudge && (
                <div className={cn("flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium", nudgeTone[nudge.tone])}>
                    <nudge.icon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{nudge.text}</span>
                </div>
            )}
        </div>
    );
}

function Avatar({ person, size = "h-9 w-9 text-xs" }) {
    return (
        <div className={cn("flex shrink-0 items-center justify-center rounded-full font-semibold", size, avatarTone(person))}
            aria-hidden="true">
            {initials(person.name)}
        </div>
    );
}

function ThreadRow({ thread, person, active, onOpen }) {
    const { latest } = thread;
    const counterpart = latest.direction === "sent"
        ? person(firstAddress(latest.to_email), latest)
        : person(latest.from_email, latest);
    const names = [...new Set(thread.messages.map(m => person(m.from_email, m).name))];
    const unanswered = latest.direction === "received";

    return (
        <button type="button" onClick={() => onOpen(latest.id)}
            aria-label={`Open email: ${thread.first.subject || "(no subject)"}`}
            className={cn(
                "group relative flex w-full items-start gap-3 px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                active ? "bg-primary/5" : "hover:bg-gray-50 dark:hover:bg-gray-800/60",
            )}>
            {unanswered && <span className="absolute left-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary" title="Awaiting your reply" />}
            <Avatar person={counterpart} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className={cn("truncate text-sm", unanswered ? "font-semibold text-gray-900 dark:text-white" : "font-medium text-gray-800 dark:text-gray-100")}>
                        {names.join(", ")}
                    </span>
                    {thread.count > 1 && (
                        <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {thread.count}
                        </span>
                    )}
                    <span className="ml-auto shrink-0 text-[11px] text-gray-400 dark:text-gray-500" title={latest.sent_at || ""}>
                        {shortTime(latest.sent_at)}
                    </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                    {latest.direction === "sent"
                        ? <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-sky-500" aria-label="Sent" />
                        : <ArrowDownLeft className="h-3.5 w-3.5 shrink-0 text-emerald-500" aria-label="Received" />}
                    <span className="truncate text-[13px] text-gray-800 dark:text-gray-200">{thread.subject}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                    <p className="min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-gray-400">
                        {latest.snippet || <span className="italic">No preview</span>}
                    </p>
                    <span className="flex shrink-0 items-center gap-2 text-gray-400">
                        {thread.hasAttachments && <Paperclip className="h-3.5 w-3.5" aria-label="Has attachments" />}
                        {thread.opens > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] text-emerald-600 dark:text-emerald-400" title={`Opened ${thread.opens}×`}>
                                <Eye className="h-3.5 w-3.5" />{thread.opens}
                            </span>
                        )}
                        {thread.clicked && <MousePointerClick className="h-3.5 w-3.5 text-purple-500" aria-label="Link clicked" />}
                        {thread.bounced && <AlertTriangle className="h-3.5 w-3.5 text-red-500" aria-label="Bounced" />}
                    </span>
                </div>
            </div>
        </button>
    );
}

export default function EmailsTab({
    emails, shownEmails, emailCounts, emailDir, setEmailDir, emailQuery, setEmailQuery,
    openEmailId, onOpen, onCompose, onSync, syncing, canSync, lastSyncedAt, contacts, accounts,
}) {
    const person = useMemo(() => makePersonResolver(contacts, accounts), [contacts, accounts]);
    const threads = useMemo(() => groupThreads(shownEmails), [shownEmails]);
    const sections = useMemo(() => {
        const out = [];
        threads.forEach(t => {
            const label = dateBucket(t.latest.sent_at);
            if (!out.length || out[out.length - 1].label !== label) out.push({ label, threads: [] });
            out[out.length - 1].threads.push(t);
        });
        return out;
    }, [threads]);

    const syncButton = canSync && (
        <button type="button" onClick={onSync} disabled={syncing}
            title={lastSyncedAt ? `Mailbox last synced ${fromNow(lastSyncedAt)}` : "Fetch this lead's emails from your connected mailboxes"}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
            <RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} />
            {syncing ? "Syncing…" : "Sync mail"}
        </button>
    );
    const composeButton = (
        <button type="button" onClick={onCompose}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary/90">
            <PenSquare className="h-3.5 w-3.5" /> Compose Email
        </button>
    );

    if (emails.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-12 text-center dark:border-gray-700">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                    <Inbox className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">No emails with this lead yet</p>
                <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">
                    Send the first one from here, or pull in past conversations from your connected mailbox.
                    Opens, clicks and replies will show up on this page.
                </p>
                <div className="mt-5 flex items-center justify-center gap-2">
                    {syncButton}
                    {composeButton}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <Overview emails={emails} person={person} />

            {/* ── toolbar ── */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-gray-700 dark:bg-gray-800/60" role="tablist" aria-label="Filter emails">
                    {[["all", "All", Mail], ["sent", "Sent", Send], ["received", "Received", Inbox]].map(([k, label, Icon]) => (
                        <button key={k} type="button" role="tab" aria-selected={emailDir === k} onClick={() => setEmailDir(k)}
                            className={cn("inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                                emailDir === k
                                    ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
                                    : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200")}>
                            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                            {label} <span className="opacity-60">{emailCounts[k]}</span>
                        </button>
                    ))}
                </div>
                <div className="relative min-w-[160px] max-w-xs flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    <input value={emailQuery} onChange={e => setEmailQuery(e.target.value)}
                        placeholder="Search subject or address" aria-label="Search emails"
                        className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-xs outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
                </div>
                <div className="ml-auto flex items-center gap-2">
                    {syncButton}
                    {composeButton}
                </div>
            </div>

            {/* ── conversations ── */}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/40">
                {threads.length === 0 && (
                    <p className="py-10 text-center text-xs text-gray-400">No emails match.</p>
                )}
                {sections.map(sec => (
                    <section key={sec.label} aria-label={sec.label}>
                        <h3 className="sticky top-0 z-[1] border-b border-gray-100 bg-gray-50/95 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 backdrop-blur dark:border-gray-800 dark:bg-gray-800/95 dark:text-gray-400">
                            {sec.label}
                        </h3>
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {sec.threads.map(t => (
                                <ThreadRow key={t.key} thread={t} person={person} onOpen={onOpen}
                                    active={t.messages.some(m => m.id === openEmailId)} />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}

export { Avatar };
